import {
  Component,
  ElementRef,
  OnInit,
  inject,
  input,
  signal,
  computed,
  effect,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DatePickerModule } from 'primeng/datepicker';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import {
  ProfessionalOperationalDetailsService,
  ProfessionalOperationalData,
} from '../../services/axa-professional-data.service';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import { FormDropdownService } from '../../../core/services/form-dropdown.service';

/** Professional types for which the AXA data section applies */
const AXA_DATA_TYPES = ['BODYSHOP', 'NETWORK', 'NK_BODYSHOP', 'HAIL_PROVIDER'] as const;
/** Professional types for which only the AUTHORITY section is shown */
const AUTHORITY_ONLY_TYPES = ['HAIL_PROVIDER'] as const;

function emptyOperationalData(): ProfessionalOperationalData {
  return {
    authority: {
      authorityType: null,
      authorityTypeOther: '',
      authorityName: '',
      authorityEmail: '',
      authorityPhone: '',
      technicalReferent: '',
      greenCertifications: '',
    },
    otherFields: {
      solCode: '',
      networkName: '',
      networkDescription: '',
      networkOrigin: '',
      webVisibility: false,
      ars: false,
      tier1: false,
      estimator: false,
      alphscaleActive: false,
      registryInsertionDate: null,
      lastRenegotiationDate: null,
      sc: '',
      scReferent: '',
      repairInterventionsCount: '',
      axaCommercialArea: '',
      notPublishedInAxa: false,
      exCode: '',
      communicationEmail: null,
      secondaryBusinessName: '',
      contract: '',
      expert: '',
      oldEmail: '',
      assignedCompanies: '',
      synergiesInternationalPaintlessDentRepairCompaniesCount: null,
      synergiesNationalPaintlessDentRepairCompaniesCount: null,
    },
  };
}

@Component({
  selector: 'app-professional-operational-data',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    RadioButtonModule,
    DatePickerModule,
    ToastModule,
  ],
  templateUrl: './axa-professional-data.component.html',
  styleUrl: './axa-professional-data.component.scss',
})
export class ProfessionalOperationalDataComponent implements OnInit {
  /** Professional ID passed from the review page */
  professionalIdInput = input<string>('');
  /** Professional type passed from the parent (optional — avoids a redundant API call) */
  professionalTypeInput = input<string>('');

  readonly today = new Date();

  private operationalDetailsService = inject(ProfessionalOperationalDetailsService);
  private professionalsService = inject(ProfessionalsService);
  private messageService = inject(MessageService);
  private elementRef = inject(ElementRef);
  private formDropdownService = inject(FormDropdownService);

  /** Resolved professional type (e.g. 'BODYSHOP', 'NETWORK', 'HAIL_PROVIDER') */
  professionalType = signal<string>('');

  isLoading = signal<boolean>(true);
  isSaving = signal<boolean>(false);
  formSubmitted = signal<boolean>(false);

  /** Emits true se i dati integrativi sono già presenti nel BE al caricamento */
  @Output() hasSavedData = new EventEmitter<boolean>();

  /** Mutable form data */
  formData = signal<ProfessionalOperationalData>(emptyOperationalData());

  /** Track touched fields for inline validation */
  touchedFields = new Set<string>();

  constructor() {
    // When the parent provides the type directly (e.g. from review-professional),
    // set it immediately and skip the getProfessionalGeneralData API call.
    effect(() => {
      const type = this.professionalTypeInput();
      if (type && type !== this.professionalType()) {
        this.professionalType.set(type);
        const id = this.professionalIdInput();
        if (id) {
          this.loadOperationalDetails(id);
        } else {
          this.isLoading.set(false);
        }
      }
    });
  }

  // ── computed ──────────────────────────────────────────────

  /** Section is applicable only for BODYSHOP, NETWORK, HAIL_PROVIDER */
  isApplicable = computed(() =>
    AXA_DATA_TYPES.includes(this.professionalType() as any)
  );

  /** Show a SOL-code-only mini section for professional types outside AXA_DATA_TYPES */
  showSolCodeSection = computed(() =>
    !this.isApplicable() && !!this.professionalType()
  );

  /** Only show "Altri Campi" section for non-HAIL_PROVIDER types */
  showOtherFields = computed(() =>
    !AUTHORITY_ONLY_TYPES.includes(this.professionalType() as any) && this.isApplicable()
  );

  /** Show Hail Provider-specific fields */
  showHailFields = computed(() => this.professionalType() === 'HAIL_PROVIDER');

  /** Show Network-specific fields */
  showNetworkFields = computed(() =>
    this.professionalType() === 'NETWORK' || this.professionalType() === 'NK_BODYSHOP'
  );

  // ── dropdown options ──────────────────────────────────────

  /** Loaded from DB via FormDropdownService (AXA_PROFESSIONAL_DATA / AUTHORITY_TYPE) */
  authorityTypeOptions = signal<{ label: string; value: string }[]>([]);

  // ── lifecycle ───────────────────────────────────

  ngOnInit(): void {
    const id = this.professionalIdInput();
    if (!id) {
      this.isLoading.set(false);
      return;
    }
    this.loadDropdownOptions();
    // If the type was already provided via professionalTypeInput, skip the API call
    if (this.professionalTypeInput()) {
      return;
    }
    this.loadProfessionalType(id);
  }
  private loadDropdownOptions(): void {
    this.formDropdownService
      .getFormDropdownValues('AXA_PROFESSIONAL_DATA', 'AUTHORITY_TYPE')
      .pipe(catchError(() => of({ status: false, message: '', data: [] })))
      .subscribe((response: any) => {
        this.authorityTypeOptions.set(
          (response.data || []).map((item: any) => ({
            label: item.valueDescription,
            value: item.valueKey,
          }))
        );
      });
  }
  private loadProfessionalType(professionalId: string): void {
    this.professionalsService
      .getProfessionalGeneralData(professionalId)
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        const data = response?.data ?? response;
        const type: string = data?.professionalType ?? '';
        this.professionalType.set(type);

        if (AXA_DATA_TYPES.includes(type as any)) {
          this.loadOperationalDetails(professionalId);
        } else if (type) {
          // Non-AXA types: still load operational details to restore any saved SOL code
          this.loadOperationalDetails(professionalId);
        } else {
          this.isLoading.set(false);
        }
      });
  }

  private loadOperationalDetails(professionalId: string): void {
    this.operationalDetailsService
      .getOperationalDetails(professionalId)
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        const data = response?.data ?? null;
        if (data) {
          const raw = { ...emptyOperationalData().otherFields, ...data.otherFields };
          this.formData.set({
            authority: { ...emptyOperationalData().authority, ...data.authority },
            otherFields: {
              ...raw,
              registryInsertionDate: raw.registryInsertionDate ? new Date(raw.registryInsertionDate as any) : null,
              lastRenegotiationDate: raw.lastRenegotiationDate ? new Date(raw.lastRenegotiationDate as any) : null,
            },
          });
          this.hasSavedData.emit(true);
        } else {
          this.hasSavedData.emit(false);
        }
        this.isLoading.set(false);
      });
  }

  // ── validation ────────────────────────────────────────────

  isFieldInvalid(field: string): boolean {
    if (!this.formSubmitted() && !this.touchedFields.has(field)) return false;
    return !this.isFieldValid(field);
  }

  private isFieldValid(field: string): boolean {
    const auth = this.formData().authority;
    const altri = this.formData().otherFields;

    switch (field) {
      case 'authorityType':
        return !!auth.authorityType;
      case 'authorityTypeOther':
        return auth.authorityType !== 'OTHER' || !!auth.authorityTypeOther?.trim();
      case 'authorityName':
        return !!auth.authorityName?.trim();
      case 'authorityEmail':
        if (!auth.authorityEmail) return true; // optional
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(auth.authorityEmail);
      case 'networkOrigin':
        return !this.showOtherFields() || !!altri.networkOrigin?.trim();
      case 'sc':
        return !this.showOtherFields() || !!altri.sc?.trim();
      case 'scReferent':
        return !this.showOtherFields() || !!altri.scReferent?.trim();
      case 'repairInterventionsCount':
        // numeric field — check presence, not string emptiness
        return !this.showOtherFields() || (altri.repairInterventionsCount != null && altri.repairInterventionsCount !== ('' as any));
      case 'axaCommercialArea':
        return !this.showOtherFields() || !!altri.axaCommercialArea?.trim();
      case 'oldEmail':
        // optional field — valid when empty; invalid only when filled with a bad format
        if (!altri.oldEmail?.trim()) return true;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(altri.oldEmail);
      case 'communicationEmail':
        return altri.communicationEmail !== null;
      case 'technicalReferent':
        return !this.showHailFields() || !!auth.technicalReferent?.trim();
      case 'registryInsertionDate':
        return !this.showOtherFields() || altri.registryInsertionDate != null;
      case 'lastRenegotiationDate':
        return !this.showOtherFields() || altri.lastRenegotiationDate != null;
      case 'networkName':
        return !this.showNetworkFields() || !!altri.networkName?.trim();
      case 'assignedCompanies':
        return !this.showNetworkFields() || !!altri.assignedCompanies?.trim();
      case 'synergiesInternationalPaintlessDentRepairCompaniesCount':
        return !this.showNetworkFields() || altri.synergiesInternationalPaintlessDentRepairCompaniesCount != null;
      case 'synergiesNationalPaintlessDentRepairCompaniesCount':
        return !this.showNetworkFields() || altri.synergiesNationalPaintlessDentRepairCompaniesCount != null;
      default:
        return true;
    }
  }

  hasValidationErrors(): boolean {
    const requiredFields = [
      'authorityType',
      'authorityTypeOther',
      'authorityName',
      'authorityEmail',
      'communicationEmail',
      ...(this.showHailFields() ? ['technicalReferent'] : []),
      ...(this.showNetworkFields() ? ['networkName', 'assignedCompanies', 'synergiesInternationalPaintlessDentRepairCompaniesCount', 'synergiesNationalPaintlessDentRepairCompaniesCount'] : []),
      ...(this.showOtherFields()
        ? ['registryInsertionDate', 'lastRenegotiationDate', 'networkOrigin', 'sc', 'scReferent', 'repairInterventionsCount', 'axaCommercialArea']
        : []),
    ];
    return requiredFields.some((f) => !this.isFieldValid(f));
  }

  // ── user interactions ──────────────────────────────────────

  onCommunicationEmailChange(value: boolean): void {
    this.formData.update((current) => ({
      ...current,
      otherFields: { ...current.otherFields, communicationEmail: value },
    }));
  }

  onAuthorityTypeChange(value: string): void {
    this.touchedFields.add('authorityType');
    if (value !== 'OTHER') {
      const current = this.formData();
      this.formData.set({
        ...current,
        authority: { ...current.authority, authorityTypeOther: '' },
      });
    }
  }

  // ── accessibility helpers ──────────────────────────────────

  /**
   * Called by the parent review component when the APPROVE action is blocked
   * due to missing required fields. Marks only the currently-invalid fields
   * as touched (so their inline errors appear), without setting formSubmitted,
   * which would otherwise block the Save button on subsequent clicks.
   */
  triggerValidation(): void {
    const requiredFields = [
      'authorityType',
      'authorityTypeOther',
      'authorityName',
      'authorityEmail',
      'communicationEmail',
      ...(this.showHailFields() ? ['technicalReferent'] : []),
      ...(this.showNetworkFields() ? ['networkName', 'assignedCompanies', 'synergiesInternationalPaintlessDentRepairCompaniesCount', 'synergiesNationalPaintlessDentRepairCompaniesCount'] : []),
      ...(this.showOtherFields()
        ? ['registryInsertionDate', 'lastRenegotiationDate', 'networkOrigin', 'sc', 'scReferent', 'repairInterventionsCount', 'axaCommercialArea']
        : []),
    ];
    requiredFields.forEach((f) => this.touchedFields.add(f));
    this.focusFirstError();
  }

  /**
   * WCAG 2.4.3 / 3.3.1: move focus to the first invalid field after a
   * failed form submission so keyboard/AT users are immediately informed.
   */
  private focusFirstError(): void {
    // Let Angular re-render the error messages (formSubmitted signal changed)
    setTimeout(() => {
      const host: HTMLElement = this.elementRef.nativeElement;
      // Prefer native invalid inputs; fallback to aria-invalid on comboboxes
      const target = host.querySelector<HTMLElement>(
        'input.ng-invalid, input[aria-invalid="true"], [role="combobox"][aria-invalid="true"]'
      );
      if (target) {
        target.focus({ preventScroll: false });
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }

  onSynergiesInternationalChange(value: number | null): void {
    this.formData.update(d => ({ ...d, otherFields: { ...d.otherFields, synergiesInternationalPaintlessDentRepairCompaniesCount: value } }));
  }

  onSynergiesNationalChange(value: number | null): void {
    this.formData.update(d => ({ ...d, otherFields: { ...d.otherFields, synergiesNationalPaintlessDentRepairCompaniesCount: value } }));
  }

  /** Saves SOL code and communicationEmail for professional types not in AXA_DATA_TYPES */
  saveSolCodeOnly(): void {
    const id = this.professionalIdInput();
    if (!id) return;
    this.isSaving.set(true);
    const payload = {
      ...emptyOperationalData(),
      otherFields: {
        ...emptyOperationalData().otherFields,
        solCode: this.formData().otherFields.solCode,
        communicationEmail: this.formData().otherFields.communicationEmail,
      },
    };
    this.operationalDetailsService
      .saveOperationalDetails(id, payload)
      .pipe(catchError((err) => { console.error('❌ Errore salvataggio Codice SOL:', err); return of(null); }))
      .subscribe((response: any) => {
        this.isSaving.set(false);
        if (!response || response?.status === false) {
          this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile salvare il Codice SOL.', life: 5000 });
        } else {
          this.messageService.add({ severity: 'success', summary: 'Salvato', detail: 'Codice SOL salvato con successo.', life: 4000 });
          this.loadOperationalDetails(id);
        }
      });
  }

  save(): void {
    this.formSubmitted.set(true);
    if (this.hasValidationErrors()) {
      this.focusFirstError();
      return;
    }

    const id = this.professionalIdInput();
    if (!id) return;

    this.isSaving.set(true);

    const toYMD = (d: Date | null): string | null =>
      d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;

    const payload = {
      ...this.formData(),
      otherFields: {
        ...this.formData().otherFields,
        registryInsertionDate: toYMD(this.formData().otherFields.registryInsertionDate) as any,
        lastRenegotiationDate: toYMD(this.formData().otherFields.lastRenegotiationDate) as any,
      },
    };

    this.operationalDetailsService
      .saveOperationalDetails(id, payload)
      .pipe(catchError((err) => { console.error('❌ Errore salvataggio dati AXA:', err); return of(null); }))
      .subscribe((response: any) => {
        this.isSaving.set(false);
        if (!response || response?.status === false) {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile salvare i dati AXA. Riprovare.',
            life: 5000,
          });
        } else {
          this.formSubmitted.set(false);
          this.touchedFields.clear();
          this.messageService.add({
            severity: 'success',
            summary: 'Salvato',
            detail: 'Dati AXA salvati con successo.',
            life: 4000,
          });
          // Reload from server to confirm data was persisted
          this.loadOperationalDetails(id);
        }
      });
  }
}
